var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { forwardRef, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { ConditionalPortal, } from "@react-md/portal";
import { useFixedPositioning, useScaleTransition, } from "@react-md/transition";
import { bem, CENTER_CENTER_ANCHOR, TOP_INNER_RIGHT_ANCHOR, } from "@react-md/utils";
import { MenuEvents } from "./MenuEvents";
import { OrientationProvider } from "./Orientation";
import { useMenu } from "./useMenu";
var block = bem("rmd-menu");
/**
 * The `Menu` component is a fully controlled component that will animate in and
 * out based on the `visible` prop as well as handle keyboard focus, closing
 * when needed, etc.
 */
export var Menu = forwardRef(function Menu(_a, forwardedRef) {
    var _b = _a.role, role = _b === void 0 ? "menu" : _b, _c = _a.tabIndex, tabIndex = _c === void 0 ? -1 : _c, controlId = _a.controlId, propStyle = _a.style, className = _a.className, visible = _a.visible, onRequestClose = _a.onRequestClose, children = _a.children, _d = _a.portal, portal = _d === void 0 ? true : _d, portalInto = _a.portalInto, portalIntoId = _a.portalIntoId, _e = _a.temporary, temporary = _e === void 0 ? true : _e, onEnter = _a.onEnter, onEntering = _a.onEntering, onEntered = _a.onEntered, onExit = _a.onExit, onExiting = _a.onExiting, onExited = _a.onExited, timeout = _a.timeout, classNames = _a.classNames, propAnchor = _a.anchor, propOnClick = _a.onClick, propOnKeyDown = _a.onKeyDown, _f = _a.defaultFocus, defaultFocus = _f === void 0 ? "first" : _f, _g = _a.horizontal, horizontal = _g === void 0 ? false : _g, positionOptions = _a.positionOptions, _h = _a.closeOnScroll, closeOnScroll = _h === void 0 ? false : _h, _j = _a.closeOnResize, closeOnResize = _j === void 0 ? false : _j, _k = _a.disableControlClickOkay, disableControlClickOkay = _k === void 0 ? false : _k, props = __rest(_a, ["role", "tabIndex", "controlId", "style", "className", "visible", "onRequestClose", "children", "portal", "portalInto", "portalIntoId", "temporary", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited", "timeout", "classNames", "anchor", "onClick", "onKeyDown", "defaultFocus", "horizontal", "positionOptions", "closeOnScroll", "closeOnResize", "disableControlClickOkay"]);
    var anchor = propAnchor;
    if (!anchor) {
        anchor = horizontal ? CENTER_CENTER_ANCHOR : TOP_INNER_RIGHT_ANCHOR;
    }
    // TODO: Refactor all the menu functionality since I made this when I had no
    // idea what I was doing with hooks
    var _l = useMenu({
        ref: forwardedRef,
        visible: visible,
        controlId: controlId,
        horizontal: horizontal,
        onClick: propOnClick,
        onKeyDown: propOnKeyDown,
        portalled: portal || typeof portalInto !== "undefined" || !!portalIntoId,
        defaultFocus: defaultFocus,
        onRequestClose: onRequestClose,
        disableControlClickOkay: disableControlClickOkay,
    }), nodeRef = _l.ref, menuRef = _l.menuRef, onClick = _l.onClick, onKeyDown = _l.onKeyDown;
    var _m = __read(useState(false), 2), cancelled = _m[0], setCancelled = _m[1];
    var prevVisible = useRef(visible);
    useEffect(function () {
        if (prevVisible.current !== visible) {
            prevVisible.current = visible;
            if (cancelled) {
                setCancelled(false);
            }
        }
    }, [visible, cancelled]);
    var fixedTo = useRef(null);
    useEffect(function () {
        fixedTo.current = document.getElementById(controlId);
    }, [controlId]);
    var _o = useFixedPositioning(__assign(__assign({}, positionOptions), { nodeRef: nodeRef, fixedTo: fixedTo, onScroll: function (_event, _a) {
            var visible = _a.visible;
            if (!closeOnScroll && visible) {
                return;
            }
            if (!visible) {
                setCancelled(true);
            }
            onRequestClose();
        }, onResize: closeOnResize ? onRequestClose : undefined, anchor: anchor, onEnter: onEnter, onEntering: onEntering, onEntered: onEntered, onExited: onExited, transformOrigin: true })), style = _o.style, transitionOptions = _o.transitionOptions;
    var _p = useScaleTransition(__assign(__assign({}, transitionOptions), { appear: temporary, className: cn(block({ horizontal: horizontal }), className), onExit: onExit, onExiting: onExiting, timeout: timeout, classNames: classNames, transitionIn: visible, temporary: temporary })), elementProps = _p.elementProps, rendered = _p.rendered;
    var orientation = horizontal ? "horizontal" : "vertical";
    return (_jsx(ConditionalPortal, __assign({ portal: portal, portalInto: portalInto, portalIntoId: portalIntoId }, { children: rendered && (_jsx(OrientationProvider, __assign({ orientation: orientation }, { children: _jsxs("div", __assign({}, props, elementProps, { "aria-orientation": orientation, role: role, tabIndex: tabIndex, style: __assign(__assign({}, propStyle), style), onClick: onClick, onKeyDown: onKeyDown }, { children: [children, _jsx(MenuEvents, { menuRef: menuRef, cancelled: cancelled, defaultFocus: defaultFocus }, void 0)] }), void 0) }), void 0)) }), void 0));
});
//# sourceMappingURL=Menu.js.map